
import React, { useMemo, useState } from "react";
import { useLeads } from "@/context/LeadContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { format, subDays, isAfter } from "date-fns";
import { el } from "date-fns/locale";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent
} from "@/components/ui/chart";
import { BarChart2, MessageSquare, Clock, Eye, TrendingUp, Users, AlertTriangle, CheckCircle } from "lucide-react";
import StatisticsCard from "@/components/dashboard/StatisticsCard";
import { PageHeader } from "@/components/layouts/PageHeader";

const calculateDateRange = (days: number) => {
  const endDate = new Date();
  const startDate = subDays(endDate, days);
  return { startDate, endDate };
};

const MessageStatistics = () => {
  const { leads } = useLeads();
  const [timeWindow, setTimeWindow] = useState<"7" | "14" | "30">("7");

  const windowDays = Number(timeWindow);

  const filteredLeads = useMemo(() => {
    const { startDate } = calculateDateRange(windowDays);
    return leads.filter(lead => isAfter(new Date(lead.updatedAt), startDate));
  }, [leads, windowDays]);

  const getMessagesPerDay = (days: number) => {
    const { startDate } = calculateDateRange(days);

    const dateLabels: string[] = [];
    const messagesPerDay: Record<string, number> = {};
    const viewsPerDay: Record<string, number> = {};

    for (let i = 0; i < days; i++) {
      const date = subDays(new Date(), i);
      const dateStr = format(date, "d MMM", { locale: el });
      dateLabels.unshift(dateStr);
      messagesPerDay[dateStr] = 0;
      viewsPerDay[dateStr] = 0;
    }

    filteredLeads.forEach(lead => {
      if (lead.smsStatus && new Date(lead.updatedAt) >= startDate) {
        const dateStr = format(new Date(lead.updatedAt), "d MMM", { locale: el });
        if (messagesPerDay[dateStr] !== undefined) {
          messagesPerDay[dateStr] += 1;
        }
      }

      if (lead.viewed && lead.viewedAt && new Date(lead.viewedAt) >= startDate) {
        const dateStr = format(new Date(lead.viewedAt), "d MMM", { locale: el });
        if (viewsPerDay[dateStr] !== undefined) {
          viewsPerDay[dateStr] += 1;
        }
      }
    });

    return dateLabels.map(date => ({
      date,
      messages: messagesPerDay[date] || 0,
      views: viewsPerDay[date] || 0
    }));
  };

  const partnerStats = useMemo(() => {
    const stats: Record<string, { partner: string, sent: number, viewed: number }> = {};

    filteredLeads.forEach(lead => {
      if (lead.smsStatus && lead.partnerName) {
        if (!stats[lead.partnerName]) {
          stats[lead.partnerName] = { partner: lead.partnerName.length > 12 ? lead.partnerName.substring(0, 12) + '...' : lead.partnerName, sent: 0, viewed: 0 };
        }
        stats[lead.partnerName].sent += 1;

        if (lead.viewed) {
          stats[lead.partnerName].viewed += 1;
        }
      }
    });

    return Object.values(stats).sort((a, b) => b.sent - a.sent);
  }, [filteredLeads]);

  const statusDistribution = useMemo(() => {
    const statuses = { sent: 0, delivered: 0, failed: 0, viewed: 0 };

    filteredLeads.forEach(lead => {
      if (lead.smsStatus === "sent") statuses.sent++;
      if (lead.smsStatus === "delivered") statuses.delivered++;
      if (lead.smsStatus === "failed") statuses.failed++;
      if (lead.viewed) statuses.viewed++;
    });

    return [
      { name: "Απεσταλμένα", value: statuses.sent, color: "#3b82f6" },
      { name: "Παραδόθηκαν", value: statuses.delivered, color: "#10b981" },
      { name: "Αποτυχία", value: statuses.failed, color: "#ef4444" },
      { name: "Αναγνώστηκαν", value: statuses.viewed, color: "#6366f1" }
    ];
  }, [filteredLeads]);

  const stats = useMemo(() => {
    const totalMessages = filteredLeads.filter(lead => lead.smsStatus).length;
    const viewedMessages = filteredLeads.filter(lead => lead.viewed).length;
    const viewRate = totalMessages > 0 ? Math.round((viewedMessages / totalMessages) * 100) : 0;

    const { startDate: currentWindowStart } = calculateDateRange(windowDays);
    const recentMessages = leads.filter(lead =>
      lead.smsStatus && isAfter(new Date(lead.updatedAt), currentWindowStart)
    ).length;

    const { startDate: previousWindowStart } = calculateDateRange(windowDays * 2);
    const previousPeriodMessages = leads.filter(lead =>
      lead.smsStatus &&
      isAfter(new Date(lead.updatedAt), previousWindowStart) &&
      !isAfter(new Date(lead.updatedAt), currentWindowStart)
    ).length;

    let trend = 0;
    let isPositive = true;
    if (previousPeriodMessages > 0) {
      const change = recentMessages - previousPeriodMessages;
      trend = Math.round((change / previousPeriodMessages) * 100);
      isPositive = change >= 0;
    }

    let avgViewTime = 0;
    let totalViewTime = 0;
    let viewsWithTime = 0;

    filteredLeads.forEach(lead => {
      if (lead.viewed && lead.viewedAt && lead.smsStatus && lead.updatedAt) {
        const sendTime = new Date(lead.updatedAt).getTime();
        const viewTime = new Date(lead.viewedAt).getTime();
        const timeDiff = (viewTime - sendTime) / (1000 * 60 * 60);

        if (timeDiff >= 0) {
          totalViewTime += timeDiff;
          viewsWithTime++;
        }
      }
    });

    if (viewsWithTime > 0) {
      avgViewTime = Math.round(totalViewTime / viewsWithTime * 10) / 10;
    }

    const failedMessages = filteredLeads.filter(lead => lead.smsStatus === "failed").length;
    const unreadMessages = totalMessages - viewedMessages;

    return { totalMessages, viewedMessages, viewRate, trend, isPositive, avgViewTime, failedMessages, unreadMessages };
  }, [filteredLeads, leads, windowDays]);

  const chartConfig = {
    messages: { label: "Μηνύματα", theme: { light: "#3b82f6", dark: "#60a5fa" } },
    views: { label: "Αναγνώσεις", theme: { light: "#6366f1", dark: "#818cf8" } }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <PageHeader
        title="Στατιστικά Μηνυμάτων"
        description="Απόδοση SMS, αναγνώσεις και επιχειρησιακές ειδοποιήσεις"
        icon={<MessageSquare />}
        iconGradient="violet"
        actions={
          <Select value={timeWindow} onValueChange={(value) => setTimeWindow(value as "7" | "14" | "30") }>
            <SelectTrigger className="w-44 bg-white border-slate-200">
              <SelectValue placeholder="Περίοδος" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Τελευταίες 7 ημέρες</SelectItem>
              <SelectItem value="14">Τελευταίες 14 ημέρες</SelectItem>
              <SelectItem value="30">Τελευταίες 30 ημέρες</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatisticsCard
          title="Σύνολο Μηνυμάτων"
          value={stats.totalMessages}
          icon={<MessageSquare />}
          variant="info"
          trend={stats.trend !== 0 ? { value: stats.trend, isPositive: stats.isPositive } : undefined}
          description="Απεσταλμένα SMS"
        />

        <StatisticsCard
          title="Αναγνώσεις"
          value={stats.viewedMessages}
          icon={<Eye />}
          variant="success"
          description="Μηνύματα που διαβάστηκαν"
        />

        <StatisticsCard
          title="Ποσοστό Αναγνώσεων"
          value={`${stats.viewRate}%`}
          icon={<TrendingUp />}
          variant={stats.viewRate > 50 ? "success" : "warning"}
          description={`Τελευταίες ${windowDays} ημέρες`}
        />

        <StatisticsCard
          title="Μέσος Χρόνος"
          value={`${stats.avgViewTime} ώρες`}
          icon={<Clock />}
          variant="info"
          description="Από αποστολή σε ανάγνωση"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Message Health Snapshot</CardTitle>
            <CardDescription>Γρήγορη εικόνα της περιόδου</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-blue-700 border-blue-200 bg-blue-50">
              Απεσταλμένα: {stats.totalMessages}
            </Badge>
            <Badge variant="outline" className="text-indigo-700 border-indigo-200 bg-indigo-50">
              Αναγνώσεις: {stats.viewedMessages}
            </Badge>
            <Badge variant="outline" className="text-slate-700 border-slate-200 bg-slate-50">
              Μη αναγνωσμένα: {stats.unreadMessages}
            </Badge>
            <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">
              Avg view: {stats.avgViewTime} ώρες
            </Badge>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Alerts</CardTitle>
            <CardDescription>Σημεία που θέλουν παρέμβαση</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-2.5">
              <span className="text-sm text-red-800">SMS Failures</span>
              <span className="font-semibold text-red-900">{stats.failedMessages}</span>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-2.5">
              <span className="text-sm text-slate-700">Μη αναγνωσμένα</span>
              <span className="font-semibold text-slate-900">{stats.unreadMessages}</span>
            </div>
            {stats.failedMessages > 0 ? (
              <Badge variant="destructive" className="w-full justify-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> Έλεγχος αποτυχιών SMS
              </Badge>
            ) : (
              <Badge variant="outline" className="w-full justify-center gap-1 text-emerald-700 border-emerald-200 bg-emerald-50">
                <CheckCircle className="h-3.5 w-3.5" /> Καμία αποτυχία στην περίοδο
              </Badge>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="daily">
        <TabsList className="bg-slate-100 p-1 rounded-lg">
          <TabsTrigger value="daily" className="rounded-md data-[state=active]:bg-white">Ημερήσια</TabsTrigger>
          <TabsTrigger value="partners" className="rounded-md data-[state=active]:bg-white">Ανά Συνεργάτη</TabsTrigger>
          <TabsTrigger value="status" className="rounded-md data-[state=active]:bg-white">Κατάσταση</TabsTrigger>
        </TabsList>

        <TabsContent value="daily" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-blue-50">
                  <BarChart2 className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Μηνύματα ανά Ημέρα</CardTitle>
                  <CardDescription>Τελευταίες {windowDays} ημέρες</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="h-80">
              <ChartContainer config={chartConfig}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={getMessagesPerDay(windowDays)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Bar dataKey="messages" name="Μηνύματα" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="views" name="Αναγνώσεις" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="partners" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-emerald-50">
                  <Users className="h-4 w-4 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Ανά Συνεργάτη</CardTitle>
                  <CardDescription>Αποστολές και αναγνώσεις</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={partnerStats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="partner" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                  <Legend />
                  <Bar dataKey="sent" name="Απεσταλμένα" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="viewed" name="Αναγνώσεις" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="status" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-violet-50">
                  <MessageSquare className="h-4 w-4 text-violet-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Κατάσταση Μηνυμάτων</CardTitle>
                  <CardDescription>Κατανομή ανά κατάσταση</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusDistribution}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={95}
                    paddingAngle={3}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: '#94a3b8' }}
                  >
                    {statusDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MessageStatistics;
